import { useSession } from "./useSession";
import type { Permissions, Task } from "../types/domain";

/** Зеркало серверного `isTaskOwnScope`: роль работает в режиме «только свои» —
 *  есть viewOwn, но нет полного view. Так ведут себя встроенный staff и любая
 *  кастомная роль с такой же матрицей. */
function isTaskOwnScope(perms: Permissions | undefined): boolean {
  const t = perms?.tasks;
  if (!t) return false;
  return t.viewOwn === true && t.view !== true;
}

/**
 * Может ли текущий пользователь создавать задачи. Берём `tasks.create` из
 * матрицы прав — работает и для встроенных ролей, и для кастомных.
 */
export function useCanWriteTasks(): boolean {
  const session = useSession();
  return session.data?.permissions?.tasks?.create === true;
}

export interface TaskPerms {
  /** Можно ли редактировать любые поля задачи (постановщицкая роль). */
  canEditAll: boolean;
  /** Можно ли вообще что-то писать в задачу (хоть status/description как
   *  исполнитель, хоть всё как создатель). */
  canEdit: boolean;
  /** Только смена status и description (assignee при scope=own). Если canEditAll
   *  = true — это тоже true. */
  canEditExecution: boolean;
  /** Можно ли удалить задачу. */
  canDelete: boolean;
  /** Можно ли менять ответственного. При scope=own — никогда. */
  canReassign: boolean;
  /** Можно ли двигать задачу по календарю (move endpoint). Это «постановщицкое»
   *  действие — только creator при scope=own или полный edit. */
  canMove: boolean;
}

/**
 * Зеркало серверной логики (assertCanModifyTask + assertCanDeleteTask) для UI.
 * Возвращает набор флагов, чтобы каждое поле в TaskModal знало, блокировать
 * себя или нет.
 *
 * Логика:
 *   - tasks.edit=false → ничего нельзя.
 *   - scope=own (viewOwn без view): создатель — всё, исполнитель — только
 *     execution-fields, остальные — ничего.
 *   - полный view: всё, что разрешено матрицей.
 */
export function useTaskPermissions(task: Task | null | undefined): TaskPerms {
  const session = useSession();
  const me = session.data;
  const empty: TaskPerms = {
    canEditAll: false,
    canEdit: false,
    canEditExecution: false,
    canDelete: false,
    canReassign: false,
    canMove: false,
  };
  if (!me || !task) return empty;
  const perms = me.permissions;
  const t = perms?.tasks;
  if (!t) return empty;

  const canDeletePerm = t.delete === true;
  const canEditPerm = t.edit === true;
  if (!canEditPerm && !canDeletePerm) return empty;

  if (!isTaskOwnScope(perms)) {
    return {
      canEditAll: canEditPerm,
      canEdit: canEditPerm,
      canEditExecution: canEditPerm,
      canDelete: canDeletePerm,
      canReassign: canEditPerm,
      canMove: canEditPerm,
    };
  }

  const isCreator = task.createdById === me.id;
  const isAssignee = task.assigneeId === me.id;
  if (isCreator) {
    return {
      canEditAll: canEditPerm,
      canEdit: canEditPerm,
      canEditExecution: canEditPerm,
      canDelete: canDeletePerm,
      canReassign: false, // scope=own не переназначает
      canMove: canEditPerm,
    };
  }
  if (isAssignee) {
    return {
      canEditAll: false,
      canEdit: canEditPerm,
      canEditExecution: canEditPerm, // только status + description
      canDelete: false,
      canReassign: false,
      canMove: false,
    };
  }
  return empty;
}

/**
 * Совместимость со старым кодом: «может ли что-то писать в задачу». Не
 * используйте для новых полей — берите конкретный флаг из useTaskPermissions.
 */
export function useCanEditTask(task: Task | null | undefined): boolean {
  return useTaskPermissions(task).canEdit;
}

/**
 * Может ли текущий пользователь удалять задачи в принципе (для UI кнопок).
 * Для конкретной задачи — useTaskPermissions(task).canDelete.
 */
export function useCanDeleteTasks(): boolean {
  const session = useSession();
  return session.data?.permissions?.tasks?.delete === true;
}
